import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

/** Shared with the inline boot script in every HTML head — keep in step. */
export const THEME_STORAGE_KEY = "datum-theme";
export const DEFAULT_THEME: Theme = "light";

function read(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // storage blocked — fall through to the default
  }
  return DEFAULT_THEME;
}

/**
 * Light/dark theme, persisted per browser.
 *
 * Deliberately does NOT follow `prefers-color-scheme`: light is the portfolio's
 * designed default and stays so until the reader chooses otherwise.
 *
 * The initial value is read synchronously so the first render already matches
 * the attribute the inline boot script set — otherwise a reader who chose dark
 * gets a white flash on every navigation.
 */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() =>
    typeof window === "undefined" ? DEFAULT_THEME : read(),
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // storage blocked — the choice simply will not persist
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggle };
}
